import { PhaseData } from "utils/MatcherProvider";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { Stack, styled, Typography, useTheme } from "@mui/material";
import colors from "assets/theme-dark/base/colors";

interface Props {
  phaseData: PhaseData;
  phaseDataBefore?: PhaseData;
}

export function DonutChart({ phaseData, phaseDataBefore }: Props): JSX.Element {
  const { palette } = useTheme();

  const options = {
    chart: {
      type: "pie",
      backgroundColor: "transparent",
    },
    title: {
      useHTML: true,
      text: `<div>
            <p style="color: ${colors.text.main}; margin-bottom:0.5rem; text-align:center; font-size:2.5rem; font-weight: bold;">
            ${phaseData.matchedCount}/${phaseData.totalRecords}
            </p>
            <p style="text-align:center; color:${palette.grey[500]}; font-size:1rem">Records matched in total</p>
            </div>`,
      verticalAlign: "middle",
    },
    plotOptions: {
      series: {
        borderWidth: 0,
      },
      type: "pie",
      size: "100%",
      innerSize: "20%",
      dataLabels: {
        distance: "-20px",
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      enabled: false,
    },
    credits: {
      enabled: false,
    },
    yAxis: {
      title: {
        text: "Values",
      },
    },
    series: [
      {
        data: [
          //matches
          {
            y: phaseData.matchedCount,
            selected: true,
            color: "rgb(42,157,144)",
          },
          //unmatched
          {
            y: phaseData.totalRecords - phaseData.matchedCount,
            selected: true,
            color: "rgb(232,196,104)",
          },
          //before this phase
          phaseDataBefore && {
            y: phaseDataBefore.matchedCount,
            selected: true,
            color: "rgb(231,110,80)",
          },
          //duplicates
          {
            y: phaseData?.duplicates?.length,
            selected: true,
            color: colors.text.main,
          },
        ].filter((x) => x),
        size: "100%",
        innerSize: "80%",
        dataLabels: {
          enabled: false,
          labelConnector: false,
        },
      },
    ],
  };

  return (
    <div
      style={{ flex: 1, gap: "1rem", display: "flex", alignItems: "center", flexDirection: "column", width: "100%" }}
    >
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        containerProps={{
          style: {
            flex: 1,
            width: "25rem",
          },
        }}
      />
      <LegendContainer>
        <Legend color={"rgb(42,157,144)"} label={"Matches this phase"}></Legend>
        <Legend color={"rgb(232,196,104)"} label={"Unmatched"}></Legend>
        <Legend color={"rgb(231,110,80)"} label={"Matches from previous phase"}></Legend>
        <Legend color={colors.text.main} label={"Duplicates"}></Legend>
      </LegendContainer>
    </div>
  );
}

const Legend = ({ color, label }: { color: string; label: string }): JSX.Element => {
  return (
    <Stack sx={{ minWidth: "100px" }} flexDirection="row" gap="0.5rem" alignItems="center">
      <div style={{ width: "1rem", height: "1rem", backgroundColor: color }} />
      <Typography color={colors.text.main} fontWeight={600}>
        {label}
      </Typography>
    </Stack>
  );
};

const LegendContainer = styled(Stack)`
  flex-direction: row;
  flex-wrap: wrap;
  gap: 2rem;
`;
